import { Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider, useAuth } from "./lib/auth";
import { LocaleProvider } from "./lib/locale";

import AcademyPage from "./pages/AcademyPage";
import AreaCalculatorPage from "./pages/AreaCalculatorPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AdminPage from "./pages/AdminPage";
import ArchitectsPage from "./pages/ArchitectsPage";
import HomePage from "./pages/HomePage";
import InterventionsPage from "./pages/InterventionsPage";
import LoginPage from "./pages/LoginPage";
import OrderEditorPage from "./pages/OrderEditorPage";
import OrderReviewPage from "./pages/OrderReviewPage";
import OrdersPage from "./pages/OrdersPage";
import SignPage from "./pages/SignPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ProjectsPage from "./pages/ProjectsPage";
import RegisterPage from "./pages/RegisterPage";
import SettingsPage from "./pages/SettingsPage";
import SurfaceDetailPage from "./pages/SurfaceDetailPage";
import ToolkitPage from "./pages/ToolkitPage";
import UsersPage from "./pages/UsersPage";
import VerifyPage from "./pages/VerifyPage";

function Splash() {
  return (
    <div className="app-surface grid min-h-screen place-items-center text-sm text-ice/60">
      Caricamento…
    </div>
  );
}

/** A temporary password must be replaced before anything else is reachable. */
function gate(user: ReturnType<typeof useAuth>["user"]) {
  return user?.mustChangePassword ? <Navigate to="/change-password" replace /> : null;
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return gate(user) ?? children;
}

/**
 * Signed-in only, without the temporary-password gate. Used by the
 * change-password page itself: wrapping it in RequireAuth would redirect the
 * page to itself and render nothing.
 */
function RequireSession({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (gate(user)) return gate(user);
  if (user.role !== "admin") return <Navigate to="/projects" replace />;
  return children;
}

function RequireStaff({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (gate(user)) return gate(user);
  if (user.role !== "admin" && user.role !== "installer" && user.role !== "architect") {
    return <Navigate to="/projects" replace />;
  }
  return children;
}

/** Orders: admin + sales. */
function RequireSales({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (gate(user)) return gate(user);
  if (user.role !== "admin" && user.role !== "sales") {
    return <Navigate to="/projects" replace />;
  }
  return children;
}

/** The technical toolkit is admin + approved architects only. */
function RequireToolkit({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (gate(user)) return gate(user);
  if (user.role !== "admin" && user.role !== "architect") {
    return <Navigate to="/projects" replace />;
  }
  return children;
}

export default function App() {
  return (
    <LocaleProvider>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify/:token" element={<VerifyPage />} />
          <Route path="/o/:token" element={<SignPage />} />
          <Route
            path="/orders"
            element={
              <RequireSales>
                <OrdersPage />
              </RequireSales>
            }
          />
          <Route
            path="/area"
            element={
              <RequireSales>
                <AreaCalculatorPage />
              </RequireSales>
            }
          />
          <Route
            path="/orders/new"
            element={
              <RequireSales>
                <OrderEditorPage />
              </RequireSales>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <RequireSales>
                <OrderEditorPage />
              </RequireSales>
            }
          />
          <Route
            path="/orders/:id/review"
            element={
              <RequireSales>
                <OrderReviewPage />
              </RequireSales>
            }
          />
          <Route
            path="/change-password"
            element={
              <RequireSession>
                <ChangePasswordPage />
              </RequireSession>
            }
          />

          <Route
            path="/projects"
            element={
              <RequireAuth>
                <ProjectsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <RequireAuth>
                <ProjectDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/surfaces/:id"
            element={
              <RequireAuth>
                <SurfaceDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/interventions"
            element={
              <RequireStaff>
                <InterventionsPage />
              </RequireStaff>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAdmin>
                <SettingsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAdmin>
                <UsersPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/architects"
            element={
              <RequireAdmin>
                <ArchitectsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/academy"
            element={
              <RequireStaff>
                <AcademyPage />
              </RequireStaff>
            }
          />
          <Route
            path="/toolkit"
            element={
              <RequireToolkit>
                <ToolkitPage />
              </RequireToolkit>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </LocaleProvider>
  );
}
